import logoJemari from '../assets/images/logo-jemari-juicy-luicy.jpg';
import logoMenaone from '../assets/images/logo-menaone.jpg';

/**
 * Branded fan-member routes (Juicy Luicy band members).
 * Each entry maps a URL slug to a fixed activity in the admin panel.
 */
/** Default blue-purple theme for generic fan routes. */
export const DEFAULT_FAN_BRAND_THEME = {
  gradient: 'from-blue-500 to-purple-600',
  gradientHover: 'hover:from-blue-700 hover:to-purple-700',
  headerGradient: 'bg-gradient-to-r from-blue-600 to-purple-600',
  logoRing: 'bg-gradient-to-r from-blue-500 to-purple-600',
  iconBg: 'bg-gradient-to-br from-blue-500 to-purple-600',
  button: 'bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700',
  titleGradient: 'bg-gradient-to-r from-blue-600 via-purple-600 to-purple-700 bg-clip-text text-transparent',
  pageBg: 'bg-gradient-to-br from-slate-50 via-blue-50 to-purple-50',
  pageBgDark: 'bg-gradient-to-br from-slate-900 via-gray-900 to-slate-800',
  cardBorder: 'border-blue-200',
  accentText: 'text-blue-600',
  accentTextHover: 'hover:text-blue-700',
  accentTextDark: 'text-blue-400',
  badge: 'bg-purple-100 text-purple-700',
  badgeDark: 'bg-purple-900/50 text-purple-300',
  memberId: 'text-purple-600',
  memberIdDark: 'text-purple-400',
  memberPrefix: 'text-blue-600',
  memberPrefixDark: 'text-blue-400',
  officialLabel: 'text-blue-600',
  officialLabelDark: 'text-blue-400',
  loader: '#7c3aed',
  loaderDark: '#8b5cf6',
  focusBorder: 'focus:border-blue-500 focus:ring-blue-500/20',
  focusRing: 'focus:ring-blue-500',
  inputFocus: 'group-focus-within:border-blue-400 group-focus-within:ring-2 group-focus-within:ring-blue-200',
  link: 'text-blue-600 hover:text-blue-700',
  linkDecoration: 'decoration-blue-300 hover:decoration-blue-400',
  stripSubtitle: 'text-blue-100',
  heroOverlay: 'bg-gradient-to-b from-purple-900/80 to-slate-900/80',
  sidebarBg: 'bg-gradient-to-br from-purple-900 to-slate-800 border-slate-700',
  registrationBg: 'bg-slate-900',
  registrationIconBg: 'bg-purple-600',
  registrationLink: 'text-purple-400 hover:text-purple-300',
  registrationButton: 'bg-purple-600 hover:bg-purple-700',
  loadingSpinnerBg: 'bg-purple-900 text-purple-400',
  confetti: 'from-blue-400 to-purple-400',
};

/** Lavender purple — Jemari / Juicy Luicy brand. */
const JEMARI_BRAND_THEME = {
  gradient: 'from-violet-400 to-purple-600',
  gradientHover: 'hover:from-violet-500 hover:to-purple-700',
  headerGradient: 'bg-gradient-to-r from-violet-600 to-purple-600',
  logoRing: 'bg-gradient-to-r from-violet-400 to-purple-500',
  iconBg: 'bg-gradient-to-br from-violet-500 to-purple-600',
  button: 'bg-gradient-to-r from-violet-600 to-purple-600 hover:from-violet-700 hover:to-purple-700',
  titleGradient: 'bg-gradient-to-r from-violet-600 via-purple-600 to-purple-700 bg-clip-text text-transparent',
  pageBg: 'bg-gradient-to-br from-violet-50 via-purple-50 to-slate-50',
  pageBgDark: 'bg-gradient-to-br from-slate-900 via-purple-950 to-slate-800',
  cardBorder: 'border-violet-200',
  accentText: 'text-violet-600',
  accentTextHover: 'hover:text-purple-600',
  accentTextDark: 'text-violet-400',
  badge: 'bg-violet-100 text-violet-700',
  badgeDark: 'bg-violet-900/50 text-violet-300',
  memberId: 'text-purple-600',
  memberIdDark: 'text-purple-400',
  memberPrefix: 'text-violet-600',
  memberPrefixDark: 'text-violet-400',
  officialLabel: 'text-violet-600',
  officialLabelDark: 'text-violet-400',
  loader: '#8C86B2',
  loaderDark: '#a78bfa',
  focusBorder: 'focus:border-violet-500 focus:ring-violet-500/20',
  focusRing: 'focus:ring-violet-500',
  inputFocus: 'group-focus-within:border-violet-400 group-focus-within:ring-2 group-focus-within:ring-violet-200',
  link: 'text-violet-600 hover:text-violet-700',
  linkDecoration: 'decoration-violet-300 hover:decoration-violet-400',
  stripSubtitle: 'text-violet-100',
  heroOverlay: 'bg-gradient-to-b from-violet-900/80 to-purple-950/80',
  sidebarBg: 'bg-gradient-to-br from-violet-900 to-purple-950 border-violet-800',
  registrationBg: 'bg-gradient-to-br from-violet-950 via-purple-950 to-slate-900',
  registrationIconBg: 'bg-violet-600',
  registrationLink: 'text-violet-400 hover:text-violet-300',
  registrationButton: 'bg-violet-600 hover:bg-violet-700',
  loadingSpinnerBg: 'bg-violet-900 text-violet-400',
  confetti: 'from-violet-400 to-purple-400',
};

/** Cyan on dark — Menaone&Only / Adrian Khalif brand. */
const MENAONE_BRAND_THEME = {
  surfaceMode: 'dark',
  gradient: 'from-cyan-400 to-teal-600',
  gradientHover: 'hover:from-cyan-500 hover:to-teal-700',
  headerGradient: 'bg-gradient-to-r from-cyan-500 to-teal-600',
  logoRing: 'bg-gradient-to-r from-cyan-400 to-teal-500',
  iconBg: 'bg-gradient-to-br from-cyan-500 to-teal-600',
  button: 'bg-gradient-to-r from-cyan-500 to-teal-600 hover:from-cyan-600 hover:to-teal-700',
  titleGradient: 'bg-gradient-to-r from-cyan-400 via-teal-500 to-teal-600 bg-clip-text text-transparent',
  pageBg: 'bg-gradient-to-br from-slate-900 via-slate-800 to-cyan-950',
  pageBgDark: 'bg-gradient-to-br from-slate-950 via-slate-900 to-cyan-950',
  cardBorder: 'border-cyan-800/50',
  accentText: 'text-cyan-500',
  accentTextHover: 'hover:text-cyan-400',
  accentTextDark: 'text-cyan-400',
  badge: 'bg-cyan-950/60 text-cyan-300',
  badgeDark: 'bg-cyan-900/40 text-cyan-300',
  memberId: 'text-cyan-400',
  memberIdDark: 'text-cyan-300',
  memberPrefix: 'text-teal-400',
  memberPrefixDark: 'text-teal-300',
  officialLabel: 'text-cyan-500',
  officialLabelDark: 'text-cyan-400',
  loader: '#06b6d4',
  loaderDark: '#22d3ee',
  focusBorder: 'focus:border-cyan-500 focus:ring-cyan-500/20',
  focusRing: 'focus:ring-cyan-500',
  inputFocus: 'group-focus-within:border-cyan-400 group-focus-within:ring-2 group-focus-within:ring-cyan-900',
  link: 'text-cyan-500 hover:text-cyan-400',
  linkDecoration: 'decoration-cyan-700 hover:decoration-cyan-500',
  stripSubtitle: 'text-cyan-100',
  heroOverlay: 'bg-gradient-to-b from-slate-900/90 via-cyan-950/70 to-slate-950/90',
  sidebarBg: 'bg-gradient-to-br from-slate-900 to-cyan-950 border-cyan-900/50',
  registrationBg: 'bg-gradient-to-br from-slate-950 via-slate-900 to-cyan-950',
  registrationIconBg: 'bg-cyan-600',
  registrationLink: 'text-cyan-400 hover:text-cyan-300',
  registrationButton: 'bg-cyan-600 hover:bg-cyan-700',
  loadingSpinnerBg: 'bg-cyan-950 text-cyan-400',
  confetti: 'from-cyan-400 to-teal-400',
};

export const FAN_MEMBERS = {
  jemari: {
    slug: 'jemari',
    activityId: '6',
    displayName: 'Jemari',
    shortLabel: 'JEMARI',
    officialMemberLabel: 'Official Member Jemari',
    logo: logoJemari,
    brandTheme: JEMARI_BRAND_THEME,
    // Printed JL cards use global registration ID — never switch to activity-scoped numbers.
    activityScopedMemberNumber: false,
  },
  menaone: {
    slug: 'menaone',
    activityId: '7',
    displayName: 'Menaone&Only',
    shortLabel: 'MENAONE&ONLY',
    officialMemberLabel: 'Official Member Menaone&Only',
    cardTitle: 'Menaone&only Adrian Khalif',
    logo: logoMenaone,
    brandTheme: MENAONE_BRAND_THEME,
    // Independent 1..N sequence; does not continue from Jemari/JL.
    activityScopedMemberNumber: true,
  },
};

export const FAN_MEMBER_LIST = Object.values(FAN_MEMBERS);

export function getFanMemberBySlug(slug) {
  return FAN_MEMBERS[slug] ?? null;
}

export function getFanMemberByActivityId(activityId) {
  const id = String(activityId);
  return FAN_MEMBER_LIST.find((member) => member.activityId === id) ?? null;
}

export function getFanMemberFromPath(pathname) {
  const slug = pathname.split('/').filter(Boolean)[0];
  return getFanMemberBySlug(slug);
}

export function isFanMemberPath(pathname) {
  return getFanMemberFromPath(pathname) != null;
}

export function getFanMemberBrandTheme(fanMember) {
  return fanMember?.brandTheme ?? DEFAULT_FAN_BRAND_THEME;
}

export function fanPath(slug, ...segments) {
  const base = `/${slug}`;
  if (!segments.length) return base;
  return `${base}/${segments.join('/')}`;
}
